import { stringify } from "qs";
import { emptyApi } from "./emptyApi";
import {
  ActorType,
  FormInstanceStatus,
  IncorporationFormSource,
  QuestionType,
} from "constants/incorporation";
import { FileObject } from "types/Models/fileObject";

export type QuestionOption = {
  uuid: string;
  label: string;
  value: string;
  order: number;
};

export type QuestionDocumentAnswer = FileObject & {
  document_answer_id: string;
};

export type QuestionAnswer =
  | { text: string }
  | { option_instance_id: string }
  | { documents: QuestionDocumentAnswer[] }
  | { text: string | null; documents: QuestionDocumentAnswer[] }
  | null;

export type Question = {
  uuid: string;
  label: string;
  question_type: QuestionType;
  is_mandatory: boolean;
  order: number;
  config: { helper_text?: string } & Record<string, unknown>;
  status: FormInstanceStatus;
  options: QuestionOption[];
  answer: QuestionAnswer;
  answered_by: string | null;
  answered_at: string | null;
};

export type Step = {
  uuid: string;
  title: string;
  description: string;
  order: number;
  actor_type: ActorType;
  status: FormInstanceStatus;
  is_locked: boolean;
  questions: Question[];
};

export type Stage = {
  uuid: string;
  title: string;
  description: string;
  order: number;
  status: FormInstanceStatus;
  is_saved_as_template: boolean;
  steps: Step[];
};

export type FormTree = {
  uuid: string;
  display_title?: string;
  title?: string;
  company_group_id?: string;
  company_group_name?: string;
  is_published: boolean;
  status: FormInstanceStatus;
  stages: Stage[];
};

export type IncorporationBaseTemplate = {
  uuid: string;
  title: string;
  created_by: string | null;
  updated_at: string;
};

export type IncorporationBaseTemplatesResponse = {
  results: IncorporationBaseTemplate[];
  total_pages: number;
  current_page: number;
  total_count: number;
  per_page: number;
};

export type SavedStageListItem = {
  source_type: "stage_template" | "stage_instance";
  source_title: string;
  uuid: string;
  title: string;
  display_name: string;
  description: string;
  order: number;
  is_saved_as_template: boolean;
  steps: Step[];
};

export type StageNodeBody = {
  parent_id?: string;
  node_id?: string;
  title?: string;
  description?: string;
  order?: number;
  is_saved_as_template?: boolean;
  saved_template_name?: string;
  steps?: unknown[];
};

export type StepNodeBody = {
  parent_id?: string;
  node_id?: string;
  title?: string;
  description?: string;
  actor_type?: ActorType;
  order?: number;
  questions?: unknown[];
};

export type QuestionOptionAction =
  | { action: "add"; label: string; value: string }
  | { action: "update"; option_id: string; label?: string; value?: string }
  | { action: "delete"; option_id: string };

export type QuestionNodeBody = {
  parent_id?: string;
  node_id?: string;
  label?: string;
  question_type?: QuestionType;
  is_mandatory?: boolean;
  config?: Record<string, unknown>;
  order?: number;
  options?: { label: string; value: string }[];
  option?: QuestionOptionAction;
  admin_status?: FormInstanceStatus;
  answer?:
    | { text: string }
    | { option_instance_id: string }
    | { document_ids: string[] }
    | { remove_document_answer_id: string };
};

export type FormTreeNodeBody =
  | { stage: StageNodeBody }
  | { step: StepNodeBody }
  | { question: QuestionNodeBody };

type FormTreeNodeResponse = Stage | Step | Question | FormTree;

type CreateFormTreeNodeArgs = { treeId: string; body: FormTreeNodeBody };
type UpdateFormTreeNodeArgs = { treeId: string; body: FormTreeNodeBody };
type DeleteFormTreeNodeArgs = { treeId: string; body: FormTreeNodeBody };

export type ReorderStageBody = { uuid: string; steps: string[] };
export type ReorderFormTreeBody = { stages: ReorderStageBody[] };
type ReorderFormTreeArgs = { treeId: string; body: ReorderFormTreeBody };

export const incorporationFormBuilderApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getFormTemplateList: build.query<
      IncorporationBaseTemplatesResponse,
      { search?: string | null; page_num?: number; page_size?: number } | void
    >({
      query: (params) => {
        const queryUrl = stringify(params || {}, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return { url: `/api/inkle/formbuilder/template/${queryUrl}` };
      },
      providesTags: ["INCORPORATION_TEMPLATES"],
    }),

    createFormTemplate: build.mutation<
      FormTree,
      { title: string; createdById?: string | null }
    >({
      query: ({ title, createdById }) => ({
        url: `/api/inkle/formbuilder/template/`,
        method: "POST",
        body: { title, created_by_id: createdById },
      }),
      invalidatesTags: ["INCORPORATION_TEMPLATES"],
    }),

    updateFormTemplateTitle: build.mutation<
      FormTree,
      { templateId: string; title: string }
    >({
      query: ({ templateId, title }) => ({
        url: `/api/inkle/formbuilder/template/${templateId}/`,
        method: "PUT",
        body: { title },
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE", "INCORPORATION_TEMPLATES"],
    }),

    deleteFormTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `/api/inkle/formbuilder/template/${templateId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["INCORPORATION_TEMPLATES"],
    }),

    getFormTemplateTree: build.query<FormTree, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `/api/inkle/formbuilder/template/${templateId}/`,
      }),
      providesTags: ["INCORPORATION_FORM_TREE"],
    }),

    createFormTemplateNode: build.mutation<
      FormTreeNodeResponse,
      CreateFormTreeNodeArgs
    >({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/template/${treeId}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        "INCORPORATION_FORM_TREE",
        "INCORPORATION_STAGE_TEMPLATES",
      ],
    }),

    updateFormTemplateNode: build.mutation<
      FormTreeNodeResponse,
      UpdateFormTreeNodeArgs
    >({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/template/${treeId}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "INCORPORATION_FORM_TREE",
        "INCORPORATION_STAGE_TEMPLATES",
      ],
    }),

    deleteFormTemplateNode: build.mutation<void, DeleteFormTreeNodeArgs>({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/template/${treeId}/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    reorderFormTemplateTree: build.mutation<FormTree, ReorderFormTreeArgs>({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/template/${treeId}/reorder/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    getFormInstanceList: build.query<
      { results: FormTree[]; count: number },
      { pageNum?: number; pageSize?: number }
    >({
      query: ({ pageNum, pageSize }) => {
        const queryUrl = stringify(
          { page_num: pageNum, page_size: pageSize },
          { skipNulls: true, addQueryPrefix: true }
        );
        return { url: `/api/inkle/formbuilder/instance/${queryUrl}` };
      },
      providesTags: ["INCORPORATION_TRACKER"],
    }),

    createFormInstance: build.mutation<
      FormTree,
      {
        formTemplateId?: string | null;
        contentTypeId: number;
        objectId: string;
      }
    >({
      query: ({ formTemplateId, contentTypeId, objectId }) => ({
        url: `/api/inkle/formbuilder/instance/`,
        method: "POST",
        body: {
          form_template_id: formTemplateId ?? null,
          content_type_id: contentTypeId,
          object_id: objectId,
        },
      }),
      invalidatesTags: ["INCORPORATION_TRACKER"],
    }),

    getFormInstanceTree: build.query<FormTree, { formInstanceId: string }>({
      query: ({ formInstanceId }) => ({
        url: `/api/inkle/formbuilder/instance/${formInstanceId}/`,
      }),
      providesTags: ["INCORPORATION_FORM_TREE"],
    }),

    getFormInstanceTreeForGroup: build.query<FormTree, { groupId: string }>({
      query: ({ groupId }) => ({
        url: `/api/inkle/incorporation/group/${groupId}/instance/`,
      }),
      providesTags: ["INCORPORATION_FORM_TREE"],
    }),

    createFormInstanceNode: build.mutation<
      FormTreeNodeResponse,
      CreateFormTreeNodeArgs
    >({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/instance/${treeId}/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    updateFormInstanceNode: build.mutation<
      FormTreeNodeResponse,
      UpdateFormTreeNodeArgs
    >({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/instance/${treeId}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    deleteFormInstanceNode: build.mutation<void, DeleteFormTreeNodeArgs>({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/instance/${treeId}/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    reorderFormInstanceTree: build.mutation<FormTree, ReorderFormTreeArgs>({
      query: ({ treeId, body }) => ({
        url: `/api/inkle/formbuilder/instance/${treeId}/reorder/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    publishFormInstance: build.mutation<
      { uuid: string; is_published: boolean },
      { formInstanceId: string; isPublished: boolean }
    >({
      query: ({ formInstanceId, isPublished }) => ({
        url: `/api/inkle/formbuilder/instance/${formInstanceId}/`,
        method: "PUT",
        body: { is_published: isPublished },
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE", "INCORPORATION_TRACKER"],
    }),

    deleteFormInstance: build.mutation<void, { formInstanceId: string }>({
      query: ({ formInstanceId }) => ({
        url: `/api/inkle/formbuilder/instance/${formInstanceId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["INCORPORATION_FORM_TREE", "INCORPORATION_TRACKER"],
    }),

    saveInstanceAsTemplate: build.mutation<
      { uuid: string },
      { formInstanceId: string; title: string }
    >({
      query: ({ formInstanceId, title }) => ({
        url: `/api/inkle/formbuilder/instance/${formInstanceId}/save_as_template/`,
        method: "POST",
        body: { title },
      }),
      invalidatesTags: [
        "INCORPORATION_TEMPLATES",
        "INCORPORATION_STAGE_TEMPLATES",
      ],
    }),

    uploadQuestionDocument: build.mutation<
      Question,
      {
        groupId: string;
        questionInstanceId: string;
        files: File[];
        text?: string;
        removeDocumentAnswerIds?: string[];
      }
    >({
      query: ({
        groupId,
        questionInstanceId,
        files,
        text,
        removeDocumentAnswerIds,
      }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        if (text !== undefined) {
          formData.append("text", text);
        }
        (removeDocumentAnswerIds ?? []).forEach((id) =>
          formData.append("remove_document_answer_ids", id)
        );
        return {
          url: `/api/inkle/incorporation/group/${groupId}/question_instance/${questionInstanceId}/document/upload/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["INCORPORATION_FORM_TREE"],
    }),

    getSavedStagesList: build.query<
      SavedStageListItem[],
      { search?: string | null }
    >({
      query: (params) => {
        const queryUrl = stringify(params, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `/api/inkle/formbuilder/stage/saved${queryUrl}`,
        };
      },
      providesTags: ["INCORPORATION_STAGE_TEMPLATES"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFormTemplateListQuery,
  useCreateFormTemplateMutation,
  useDeleteFormTemplateMutation,
  useUpdateFormTemplateTitleMutation,
  useGetFormTemplateTreeQuery,
  useCreateFormTemplateNodeMutation,
  useUpdateFormTemplateNodeMutation,
  useDeleteFormTemplateNodeMutation,
  useReorderFormTemplateTreeMutation,
  useGetFormInstanceListQuery,
  useCreateFormInstanceMutation,
  useGetFormInstanceTreeQuery,
  useGetFormInstanceTreeForGroupQuery,
  useCreateFormInstanceNodeMutation,
  useUpdateFormInstanceNodeMutation,
  useDeleteFormInstanceNodeMutation,
  useReorderFormInstanceTreeMutation,
  usePublishFormInstanceMutation,
  useDeleteFormInstanceMutation,
  useSaveInstanceAsTemplateMutation,
  useUploadQuestionDocumentMutation,
  useGetSavedStagesListQuery,
} = incorporationFormBuilderApis;
